import React, { ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';

import { Button } from '@ui/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';

import { useCreateChapter, useUpdateChapter } from '@data/hooks/organization';
import type { Chapter } from '@data/utils/types';

const KEY_REGEX = /^[A-Z0-9_]+$/;
const KEY_MAX = 16;
// Matches `ChapterValidator.label` on the API — keeping these in sync avoids labels that pass UI
// validation but get rejected at the boundary (or vice-versa).
const LABEL_MAX = 100;

type FormValues = {
  key: string;
  label: string;
};

interface ChapterFormDialogProps {
  organizationId: string;
  chapter: Chapter | null;
  open: boolean;
  onClose: () => void;
}

const ChapterFormDialog = ({ organizationId, chapter, open, onClose }: ChapterFormDialogProps): ReactElement => {
  const { t } = useTranslation();
  const isEdit = chapter !== null;

  const createChapter = useCreateChapter(organizationId);
  const updateChapter = useUpdateChapter(organizationId);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting, isValid },
  } = useForm<FormValues>({
    defaultValues: { key: '', label: '' },
    mode: 'onChange',
  });

  useEffect(() => {
    if (open) {
      reset({ key: chapter?.key ?? '', label: chapter?.label ?? '' });
    }
  }, [open, chapter, reset]);

  const onSubmit = (values: FormValues) => {
    if (isEdit && chapter) {
      // Only the label is editable on existing chapters (key is immutable, isActive is handled inline).
      updateChapter.mutate(
        { chapterId: chapter.id, values: { label: values.label } },
        {
          onSuccess: () => onClose(),
          onError: () => setError('root', { message: t('chaptersAdmin.form.saveFailed') }),
        },
      );
    } else {
      createChapter.mutate(values, {
        onSuccess: () => onClose(),
        onError: error => {
          const status = (error as { response?: { status?: number } })?.response?.status;
          if (status === 409) {
            setError('key', { message: t('chaptersAdmin.form.duplicate') });
          } else {
            setError('root', { message: t('chaptersAdmin.form.saveFailed') });
          }
        },
      });
    }
  };

  const isPending = createChapter.isPending || updateChapter.isPending || isSubmitting;

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>
              {isEdit ? t('chaptersAdmin.form.title.edit') : t('chaptersAdmin.form.title.create')}
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4 py-4">
            <Controller
              name="key"
              control={control}
              rules={{
                required: t('chaptersAdmin.form.required'),
                maxLength: { value: KEY_MAX, message: t('chaptersAdmin.form.maxLength', { max: KEY_MAX }) },
                pattern: { value: KEY_REGEX, message: t('chaptersAdmin.form.keyInvalid') },
              }}
              render={({ field }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="chapter-form-key">{t('chaptersAdmin.form.key')}</Label>
                  <Input
                    id="chapter-form-key"
                    {...field}
                    disabled={isEdit || isPending}
                    placeholder={t('chaptersAdmin.form.keyPlaceholder')}
                    autoComplete="off"
                    aria-describedby="chapter-form-key-hint"
                  />
                  <p id="chapter-form-key-hint" className="text-xs text-muted-foreground">
                    {t('chaptersAdmin.form.keyHint')}
                  </p>
                  {errors.key?.message ? <p className="text-sm text-destructive">{errors.key.message}</p> : null}
                </div>
              )}
            />

            <Controller
              name="label"
              control={control}
              rules={{
                required: t('chaptersAdmin.form.required'),
                maxLength: { value: LABEL_MAX, message: t('chaptersAdmin.form.maxLength', { max: LABEL_MAX }) },
              }}
              render={({ field }) => (
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="chapter-form-label">{t('chaptersAdmin.form.label')}</Label>
                  <Input id="chapter-form-label" {...field} disabled={isPending} autoComplete="off" />
                  {errors.label?.message ? <p className="text-sm text-destructive">{errors.label.message}</p> : null}
                </div>
              )}
            />

            {errors.root?.message ? <p className="text-sm text-destructive">{errors.root.message}</p> : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              {t('chaptersAdmin.form.cancel')}
            </Button>
            <Button type="submit" disabled={isPending || !isValid}>
              {t('chaptersAdmin.form.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChapterFormDialog;
